import { create } from 'zustand';
import type {
  CategorySel,
  CreatorCategory,
  CreatorSex,
  CreatorStatus,
  CreatorType,
} from '@/types';

type SortDir = 'asc' | 'desc';
export type ListView = 'active' | 'alerted' | 'trash';

export type AlertedFilter =
  | 'orphaned'
  | 'with_active_owner'
  | 'ai_pending'
  | 'ai_rejected'
  | 'ai_validated'
  | 'appeal_pending'
  | 'discarded';

// Filtros del dropdown "Alertas" cuya cláusula exige `alert_pending_at !=
// ""`. Solo aplican en la pestaña "Alertados": "Activos" excluye los
// alertados por definición y en "Papelera" no hay ninguno (borrar pone
// is_void=true y lead-attention-flag.pb.js limpia la alerta). Fuera de
// "Alertados" ni se ofrecen ni se conservan al cambiar de pestaña.
export const ALERT_SCOPED_FILTERS: AlertedFilter[] = [
  'orphaned',
  'with_active_owner',
  'ai_rejected',
];

function nextAlertedFilter(
  current: AlertedFilter | undefined,
  view: ListView
): AlertedFilter | undefined {
  if (!current || view === 'alerted') return current;
  return ALERT_SCOPED_FILTERS.includes(current) ? undefined : current;
}

type LeadFilterState = {
  category: CreatorCategory | undefined;
  creator_type: CreatorType | undefined;
  rank: number | undefined;
  collector: string | undefined;
  status: CreatorStatus | undefined;
  sex: CreatorSex | undefined;
  search: string;
  alertedFilter: AlertedFilter | undefined;
  page: number;
  sortKey: string;
  sortDir: SortDir;
  view: ListView;
};

type LeadFilterActions = {
  setCategorySel: (v: CategorySel | undefined) => void;
  setRank: (v: number | undefined) => void;
  setCollector: (v: string | undefined) => void;
  setStatus: (v: CreatorStatus | undefined) => void;
  setSex: (v: CreatorSex | undefined) => void;
  setSearch: (v: string) => void;
  setAlertedFilter: (v: AlertedFilter | undefined) => void;
  setPage: (p: number) => void;
  setSort: (key: string, dir: SortDir) => void;
  setView: (v: ListView) => void;
  clearFilters: () => void;
};

const INITIAL: LeadFilterState = {
  category: undefined,
  creator_type: undefined,
  rank: undefined,
  collector: undefined,
  status: undefined,
  sex: undefined,
  search: '',
  alertedFilter: undefined,
  page: 1,
  sortKey: 'created',
  sortDir: 'desc',
  view: 'active',
};

export const useCreatorFilters = create<LeadFilterState & LeadFilterActions>(
  (set) => ({
    ...INITIAL,
    setCategorySel: (v) =>
      set({ category: v?.category, creator_type: v?.type, page: 1 }),
    setRank: (v) => set({ rank: v, page: 1 }),
    setCollector: (v) => set({ collector: v, page: 1 }),
    setStatus: (v) => set({ status: v, page: 1 }),
    setSex: (v) => set({ sex: v, page: 1 }),
    setSearch: (v) => set({ search: v, page: 1 }),
    setAlertedFilter: (v) => set({ alertedFilter: v, page: 1 }),
    setPage: (p) => set({ page: p }),
    setSort: (key, dir) => set({ sortKey: key, sortDir: dir, page: 1 }),
    setView: (v) =>
      set((s) => ({
        view: v,
        page: 1,
        alertedFilter: nextAlertedFilter(s.alertedFilter, v),
      })),
    clearFilters: () => set(INITIAL),
  })
);
